import { createContext, ReactNode, useMemo } from 'react';

// project import
import defaultConfig from 'config';
import useLocalStorage from 'hooks/useLocalStorage';

// types
import { CustomizationProps, FontFamily, I18n, PresetColor, ThemeDirection, ThemeMode } from 'types/config';

// Valid preset colors for validation
const VALID_PRESET_COLORS: PresetColor[] = ['default', 'theme1', 'theme2', 'theme3', 'theme4', 'theme5', 'theme6', 'theme7', 'theme8', 'theme9'];
const VALID_MODES: ThemeMode[] = ['light', 'dark'];

// initial state
const initialState: CustomizationProps = {
  ...defaultConfig,
  onChangeContainer: () => {},
  onChangeLocalization: (lang: I18n) => {},
  onChangeMode: (mode: ThemeMode) => {},
  onChangePresetColor: (theme: PresetColor) => {},
  onChangeDirection: (direction: ThemeDirection) => {},
  onChangeMiniDrawer: (miniDrawer: boolean) => {},
  onChangeFontFamily: (fontFamily: FontFamily) => {}
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

function ConfigProvider({ children }: ConfigProviderProps) {
  const [storedConfig, setConfig] = useLocalStorage('theme-config', initialState);

  // Validate and sanitize the config to ensure all values are valid
  const config = useMemo(() => {
    const sanitized = { ...storedConfig };

    // Validate presetColor - if invalid, use default
    if (!VALID_PRESET_COLORS.includes(sanitized.presetColor)) {
      console.warn(`Invalid presetColor "${sanitized.presetColor}", falling back to "${defaultConfig.presetColor}"`);
      sanitized.presetColor = defaultConfig.presetColor;
    }

    // Validate mode - if invalid, use default
    if (!VALID_MODES.includes(sanitized.mode)) {
      console.warn(`Invalid mode "${sanitized.mode}", falling back to "${defaultConfig.mode}"`);
      sanitized.mode = defaultConfig.mode;
    }

    return sanitized;
  }, [storedConfig]);

  const onChangeContainer = () => {
    setConfig({
      ...config,
      container: !config.container
    });
  };

  const onChangeLocalization = (lang: I18n) => {
    setConfig({
      ...config,
      i18n: lang
    });
  };

  const onChangeMode = (mode: ThemeMode) => {
    setConfig({
      ...config,
      mode
    });
  };

  const onChangePresetColor = (theme: PresetColor) => {
    setConfig({
      ...config,
      presetColor: theme
    });
  };

  const onChangeDirection = (direction: ThemeDirection) => {
    setConfig({
      ...config,
      themeDirection: direction
    });
  };

  const onChangeMiniDrawer = (miniDrawer: boolean) => {
    setConfig({
      ...config,
      miniDrawer
    });
  };

  const onChangeFontFamily = (fontFamily: FontFamily) => {
    setConfig({
      ...config,
      fontFamily
    });
  };

  return (
    <ConfigContext.Provider
      value={{
        ...config,
        onChangeContainer,
        onChangeLocalization,
        onChangeMode,
        onChangePresetColor,
        onChangeDirection,
        onChangeMiniDrawer,
        onChangeFontFamily
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };
